import React from 'react';
import { useCutscene } from '../../city/cutscene';
import { useCityStore } from '../../city/cityStore';

// The flat half of a cutscene (city/cutscene, components/city/CityCutscene):
//   · the cue — offered only to someone already there: "Avva Digital and
//     Samzie are talking · Step in", or "Malaki is live on the plaza stage ·
//     Watch" (scene view for a set, until it ends)
//   · during a cut: letterbox, the line being said as a caption, and Leave
//   · between cuts (yours again): "Following …" and Leave
// Leave is ALWAYS on screen once you have stepped in (every wait needs a door).

const ACCENT = '#ffb25e';

const chip = {
  position: 'fixed', left: '50%', top: 62, transform: 'translateX(-50%)', zIndex: 45,
  display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px 7px 14px', borderRadius: 999,
  background: 'rgba(16,10,6,0.86)', border: '1px solid rgba(255,178,94,0.45)', color: '#ffe9c4',
  font: '600 13px/1.2 system-ui, sans-serif', boxShadow: '0 8px 26px rgba(0,0,0,0.45)',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', maxWidth: 'calc(100vw - 32px)', boxSizing: 'border-box',
  pointerEvents: 'auto',
};
const btn = {
  border: 0, borderRadius: 999, padding: '7px 13px', background: ACCENT, color: '#1a1205',
  font: '800 12.5px system-ui', cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none',
};
const ghost = { ...btn, background: 'transparent', color: '#ffe9c4', border: '1px solid rgba(255,178,94,0.45)' };
const dot = { width: 8, height: 8, borderRadius: 99, background: ACCENT, boxShadow: `0 0 10px ${ACCENT}`, flex: 'none' };
const bar = (top) => ({
  position: 'fixed', left: 0, right: 0, [top ? 'top' : 'bottom']: 0, height: '11vh', minHeight: 54, zIndex: 60,
  background: '#000', pointerEvents: 'none', transition: 'opacity 0.4s', display: 'flex', alignItems: 'center', justifyContent: 'center',
});

// Voiced conversations are for paying members; anyone else gets one a visit
// (Khari, 2026-10-23). Said once per conversation, quietly, with a way out.
const PLANS_URL = 'https://www.hypetunes.com/plans';
function FreeNote() {
  const capped = useCityStore((s) => s.capped);
  const [closed, setClosed] = React.useState(() => new Set());
  if (!capped || closed.has(capped)) return null;
  return (
    <div style={{ ...chip, top: 'auto', bottom: 'calc(max(16px, env(safe-area-inset-bottom)) + var(--hud-player-h, 0px) + 74px)' }} role="status">
      <span style={dot} />
      <span style={{ fontWeight: 500 }}>You&apos;ve heard this visit&apos;s free conversation. Members hear every one.</span>
      <a href={PLANS_URL} target="_blank" rel="noopener noreferrer" style={{ ...btn, textDecoration: 'none' }}>See plans</a>
      <button type="button" style={{ ...ghost, padding: '7px 10px' }} aria-label="Dismiss" onClick={() => setClosed((s) => new Set([...s, capped]))}>✕</button>
    </div>
  );
}

export default function CityCutsceneOverlay() {
  return (<><CutsceneChips /><FreeNote /></>);
}

function CutsceneChips() {
  const cue = useCutscene((s) => s.cue);
  const target = useCutscene((s) => s.target);
  const phase = useCutscene((s) => s.phase);
  const caption = useCutscene((s) => s.caption);
  const followName = useCutscene((s) => s.followName);
  const followWay = useCutscene((s) => s.followWay);
  const stepIn = useCutscene((s) => s.stepIn);
  const leave = useCutscene((s) => s.leave);
  const offer = useCutscene((s) => s.offer);
  const setOffer = useCutscene((s) => s.setOffer);
  const dismissCue = useCutscene((s) => s.dismissCue);
  // WALKING WITH AN ARTIST (game/player walkWith): Escape ends it, like Done
  // (Khari: "pressing done or escaping from the follow should return to normal
  // mode"). Not while typing: Escape in a text box is the box's.
  const walking = target?.kind === 'artist';
  React.useEffect(() => {
    if (!walking) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      leave();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [walking, leave]);

  // The camera held with no scene of theirs (a chat scene, a moment's still): no
  // cue, no offer, no bar — whatever holds it draws its own.
  if (phase === 'cut' && !target) return null;

  if (!target && offer && Date.now() < offer.until) {
    return (
      <div style={chip} role="status">
        <span style={dot} />
        <span>{offer.title}?</span>
        <button type="button" style={btn} onClick={() => stepIn({ kind: 'artist', id: offer.id, title: offer.full || offer.title })}>Walk with them</button>
        <button type="button" style={ghost} onClick={() => setOffer(null)}>Not now</button>
      </div>
    );
  }
  if (!target) {
    if (!cue) return null;
    return (
      <div style={chip} role="status">
        <span style={dot} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {cue.title} <span style={{ color: 'rgba(255,233,196,0.65)', fontWeight: 500 }}>{cue.sub}</span>
        </span>
        <button type="button" style={btn} onClick={() => stepIn({ kind: cue.kind, id: cue.id, title: cue.full || cue.title })}>{cue.cta || 'Step in'}</button>
        <button type="button" style={{ ...ghost, padding: '7px 10px' }} aria-label="Dismiss" title="Not this one" onClick={dismissCue}>✕</button>
      </div>
    );
  }

  if (phase === 'cut') {
    return (
      <>
        <div style={bar(true)}>
          <span style={{ color: 'rgba(255,233,196,0.55)', font: '700 11px system-ui', letterSpacing: 1.6, textTransform: 'uppercase' }}>{target.title}</span>
        </div>
        <div style={{ ...bar(false), padding: '0 16px', boxSizing: 'border-box' }}>
          {caption && (
            <div style={{ maxWidth: 760, textAlign: 'center', color: '#fff3df', font: '500 15px/1.35 system-ui, sans-serif' }}>
              <span style={{ color: ACCENT, fontWeight: 800, marginRight: 8 }}>{caption.name}</span>
              {caption.text}
            </div>
          )}
        </div>
        <button
          type="button" onClick={leave}
          style={{ ...ghost, position: 'fixed', zIndex: 61, top: 'max(12px, calc(5.5vh - 16px))', right: 16, background: 'rgba(0,0,0,0.4)' }}
        >{target.kind === 'greet' ? 'Skip' : 'Leave'}</button>
      </>
    );
  }

  // Yours again, between scenes: who you are following, and which way they are.
  return (
    <div style={chip} role="status">
      {followWay ? (
        <span aria-hidden style={{ display: 'inline-block', transform: `rotate(${followWay.deg}deg)`, color: ACCENT, fontSize: 15, lineHeight: 1, width: 14, textAlign: 'center' }}>↑</span>
      ) : <span style={dot} />}
      <span>
        {followName ? <>{walking ? 'Walking with' : 'Following'} <b>{followName}</b></> : target.title}
        {followWay && followWay.dist > 4 && <span style={{ color: 'rgba(255,233,196,0.6)', fontWeight: 500 }}> · {followWay.dist} ahead</span>}
      </span>
      <button type="button" style={ghost} onClick={leave} title={walking ? 'Back to walking yourself (Esc)' : undefined}>{walking ? 'Done' : 'Leave'}</button>
    </div>
  );
}
